import React, { useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import CloseButton from '../atoms/CloseButton';
import QuickSearchAutoComplete from '../molecules/QuickSearchAutoComplete';
import { SearchByNamePlayer } from './SearchPlayers';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { COLOR_BLUE, COLOR_BORDER, COLOR_DARK_BLUE, COLOR_TEXT } from '../styles/colors';
import { getPlayerPath } from '../services/path';
import { State } from '../redux/reducers';
import QuickSearchPlayer from '../types/QuickSearchPlayer';

interface SearchPlayerNamesProps {
  className?: string;
  playerTypes?: string[];
  searchByNamePlayers: SearchByNamePlayer[];
  setSearchByNamePlayers: (searchByNamePlayers:SearchByNamePlayer[]) => void;
}

const COLLAPSED_LIMIT = 8;

const useStyles = makeStyles(theme => ({
  searchPlayerNames: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },

  autoComplete: {
    width: '100%',
  },

  addButton: {
    width: 52,
    height: 52,
    marginLeft: theme.spacing(1),
    color: COLOR_BLUE,
    background: 'none',
    border: 'none',

    '&:hover': {
      color: COLOR_DARK_BLUE,
      background: 'none',
      border: 'none',
    },
  },

  names: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexWrap: 'wrap',
    listStyle: 'none',
    padding: 0,
    margin: 0,
  },
  name: {
    height: 24,
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
    padding: theme.spacing(0, 0.25, 0, 1),
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1,
    color: COLOR_TEXT,
    border: `1px solid ${COLOR_BORDER}`,
    borderRadius: 6,

    '&:last-of-type': {
      marginRight: 0,
    },
  },

  playerLink: {
    color: COLOR_BLUE,
    textDecoration: 'none',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },

  removeButton: {
    marginLeft: theme.spacing(0.5),
    transform: 'scale(0.78)',
  },

  collapseButton: {
    marginTop: theme.spacing(2),
    appearance: 'none',
    border: 0,
    padding: 0,
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1,
    color: COLOR_BLUE,
    background: 'none',
    outline: 'none',
    cursor: 'pointer',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
  },
  collapseText: {
    textDecoration: 'underline',
  },
  collapseArrow: {
    marginLeft: theme.spacing(1),
  },
}), { name: SearchPlayerNames.name });

export default function SearchPlayerNames (props:SearchPlayerNamesProps) {
  const {
    className,
    playerTypes,
    searchByNamePlayers,
    setSearchByNamePlayers,
  } = props;
  const classes = useStyles();
  const newProfileEnabled = useSelector((state:State) => Boolean(state.user?.program?.newProfileEnabled));

  const [collapsed, setCollapsed] = useState<boolean>(true);

  function onEnter (searchText:string) {
    if (searchText.length > 2) {
      setSearchByNamePlayers([
        ...searchByNamePlayers,
        { name: searchText } as SearchByNamePlayer,
      ]);
    }
  }

  function onSelect (player:QuickSearchPlayer|null) {
    if (player) {
      setSearchByNamePlayers([
        ...searchByNamePlayers,
        {
          name: `${player.firstName} ${player.lastName}`,
          slug: player.slug,
        } as SearchByNamePlayer,
      ]);
    }
  }

  function onRemove (index:number) {
    return () => {
      const mutatedPlayers = [...searchByNamePlayers];
      mutatedPlayers.splice(index, 1);

      setSearchByNamePlayers(mutatedPlayers);
    };
  }

  const playersToShow:SearchByNamePlayer[] = collapsed
    ? searchByNamePlayers.slice(0, COLLAPSED_LIMIT)
    : searchByNamePlayers;

  return (
    <div className={clsx(classes.searchPlayerNames, className)}>
      <QuickSearchAutoComplete
        className={classes.autoComplete}
        label='Search by Name'
        playerTypes={playerTypes}
        onEnter={onEnter}
        onSelect={onSelect}
        clearOnAction
      />

      {!!searchByNamePlayers?.length && (
        <ul className={classes.names}>
          {playersToShow.map((player:SearchByNamePlayer, index:number) => (
            <li
              key={index}
              className={classes.name}
            >
              {!!player.slug
                ? <Link
                    className={classes.playerLink}
                    to={getPlayerPath(player.slug, newProfileEnabled)}
                    target='_blank'
                  >{player.name}</Link>
                : player.name
              }

              <CloseButton
                className={classes.removeButton}
                onClick={onRemove(index)}
              />
            </li>
          ))}
        </ul>
      )}

      {searchByNamePlayers?.length > COLLAPSED_LIMIT && (
        <button
          className={classes.collapseButton}
          onClick={() => setCollapsed(!collapsed)}
        >
          <span className={classes.collapseText}>
            Show {collapsed ? 'All' : 'Less'}
          </span>

          <span className={classes.collapseArrow}>
            {collapsed ? <>&#9660;</> : <>&#9650;</>}
          </span>
        </button>
      )}
    </div>
  );
}
